import React, { useMemo } from "react";
import { Class } from "@common/types/class";
import { ClassWithChildren } from "@helpers/class";
import { AvatarTooltip } from "@components/avatar/AvatarTooltip";
import { ClassAvatar } from "@components/avatar/ClassAvatar";
import { ClassAvatarGroup } from "@components/avatar/ClassAvatarGroup";
import colors from "@styles/colors.module.scss";
import styles from "./JournalEntryCells.module.scss";

interface ClassDiffCellProps {
    classIds: string[];
    oldClassIds?: string[];
    classesMap: Map<string, ClassWithChildren>;
    hasChanges?: boolean;
    isAdded?: boolean;
    isRemoved?: boolean;
}

function resolveClasses(
    ids: string[],
    classesMap: Map<string, ClassWithChildren>,
): Class[] {
    return ids.map((id) => classesMap.get(id)).filter(Boolean) as Class[];
}

const GreyClassIcons = ({ classes }: { classes: Class[] }) => {
    if (classes.length === 0) {
        return null;
    }
    return (
        <span className={styles.oldClassIcons}>
            {classes.map((c) => (
                <AvatarTooltip key={c.id} content={c.label}>
                    <ClassAvatar
                        class={{ ...c, iconColor: colors.gray300 }}
                        size="sm"
                        copyId={c.id}
                    />
                </AvatarTooltip>
            ))}
        </span>
    );
};

export const ClassDiffCell: React.FC<ClassDiffCellProps> = ({
    classIds,
    oldClassIds,
    classesMap,
    hasChanges,
    isAdded,
    isRemoved,
}) => {
    const currentClasses = useMemo(
        () => resolveClasses(classIds ?? [], classesMap),
        [classIds, classesMap],
    );

    const oldClasses = useMemo(
        () => resolveClasses(oldClassIds ?? [], classesMap),
        [oldClassIds, classesMap],
    );

    if (currentClasses.length === 0 && oldClasses.length === 0) {
        return null;
    }

    if (isRemoved) {
        return <GreyClassIcons classes={currentClasses} />;
    }

    if (isAdded) {
        return <ClassAvatarGroup classes={currentClasses} max={3} />;
    }

    const oldKey = (oldClassIds ?? [])
        .toSorted((a, b) => a.localeCompare(b))
        .join(",");
    const newKey = (classIds ?? [])
        .toSorted((a, b) => a.localeCompare(b))
        .join(",");
    const classesChanged = hasChanges && oldKey !== newKey;

    if (!classesChanged) {
        return <ClassAvatarGroup classes={currentClasses} max={3} />;
    }

    return (
        <div className={styles.diffContainer}>
            <GreyClassIcons classes={oldClasses} />
            <ClassAvatarGroup classes={currentClasses} max={3} />
        </div>
    );
};
